<template>
  <div class="bg-secondary shadow-deviceBoxShadow p-32 rounded">
    <!-- Start top Bar -->
    <div class="top-bar">
      <div class="w-max">
        <v-dropdown
          placement="bottom-end"
          class="p-0"
          v-if="isOwnerAndAdmin || hasPermission('zones_and_devices_management')"
        >
          <div class="notification-box">
            <vector />
          </div>

          <template #popper>
            <ul class="py-10">
              <li
                class="popover-group_li hover-popover-group_li"
                v-close-popper="true"
                @click="$router.push({ name: 'EditDevice', params: { id: deviceId } })"
              >
                <div class="popover_classes">
                  <div class="w-2/3">
                    {{ $t('edit') }}
                  </div>
                  <edit class="popover-img" />
                </div>
              </li>
              <li
                class="popover-group_li hover-popover-group_li"
                v-close-popper="true"
                @click="$router.push({ name: 'DeviceAccess', params: { id: deviceId } })"
              >
                <div class="popover_classes">
                  <div class="w-2/3">
                    {{ $t('device_access') }}
                  </div>
                  <key class="popover-img" />
                </div>
              </li>
              <li
                class="popover-group_li hover-popover-group_li"
                v-close-popper="true"
                @click="$router.push({ name: 'Log', params: { id: deviceId } })"
              >
                <div class="popover_classes">
                  <div class="w-2/3">
                    {{ $t('history') }}
                  </div>
                  <history class="popover-img" />
                </div>
              </li>
            </ul>
          </template>
        </v-dropdown>
      </div>
      <div
        class="flex-gap"
        :class="
          isOwnerAndAdmin || hasPermission('zones_and_devices_management')
            ? 'mx-11 lg:mx-0 lg:my-11'
            : 'flex-gap-margin'
        "
      >
        <div class="lg:w-full">
          <p class="text-18 font-bold">{{ deviceData.name }}</p>
          <div class="flex flex-row items-center gap-x-10" v-if="deviceData.connected">
            <span>{{ $t('connected') }}</span>
            <span class="device-icon-connected mx-1" />
          </div>
          <div class="flex flex-row items-center gap-x-10" v-else>
            <span>{{ $t('disconnected') }}</span>
            <span class="device-icon-disconnected mx-1" />
          </div>
        </div>
        <div class="flex items-center gap-x-20">
          <base-button
            height="48"
            width="250"
            buttonType="info-opacity"
            @click.native="openMessageModal()"
            :disabled="!hasPermission('zones_and_devices_management')"
          >
            <template #text>
              <span class="text-18 sm:text-12">{{ $t('set_message') }}</span>
            </template>
          </base-button>
          <base-button
            radius="default"
            buttonType="success"
            class="lg:w-full"
            width="250"
            height="48"
            @click.native="unlockDeviceHandler"
            :disabled="deviceData.can_unlock === false || deviceData.can_unlock === null"
            data-cy="unlock-button"
          >
            <template #text>
              <div class="unlock-button">
                <img :src="lockIcon" class="px-1" />
                <div class="font-medium">
                  {{ $t('unlock') }}
                </div>
              </div>
            </template>
          </base-button>
        </div>
      </div>
      <div class="flex items-center justify-between w-max lg:order-first" @click="$router.go(-1)">
        <div class="back-icon">
            <back-arrow class="m-auto right-back-arrow" />
          </div>
      </div>
    </div>
    <!-- end top Bar -->

    <!-- Start Message Bar -->
    <div class="mt-16">
      <div>
        <div class="tab-content mt-20">
          <!-- member component start -->
          <div class="member-card" v-if="isLoading">
            <BaseSkeleton
              v-for="index in 12"
              :key="index"
              for="deviceMembers"
              name="deviceMembers"
              width="full"
              height="180"
            />
          </div>
          <div v-else>
            <div v-if="deviceMembers" class="mt-16">
              <p class="font-semibold text-20 mb-1">{{ $t('members') }}</p>
              <div class="member-card">
                <div v-for="member in deviceMembers" :key="member.id">
                  <figure
                    :class="theme === 'light' ? 'shadow-dashboardCard' : 'shadow-dashboardCardDark'"
                    class="member-card-main pt-24"
                  >
                    <figcaption class="member-card-text">
                      <div class="flex justify-center pb-10 relative">
                        <img
                          v-if="member.avatar != null"
                          :src="resizer(member.avatar)"
                          class="member-card-image"
                          @error="OnImageError"
                        />
                        <UserIcon v-else class="member-card-image" />
                        <owner-icon alt="is-owner" class="is-owner" v-if="member.is_owner === 1" />
                      </div>
                      <h3
                        class="text-sm font-bold"
                        :class="{ 'text-primary': member.is_owner === 1 }"
                      >
                        {{ member.name }}
                      </h3>
                      <h5 class="text-12 text-subtitle">{{ member.job_position }}</h5>
                    </figcaption>
                  </figure>
                </div>
              </div>
            </div>
          </div>
          <!-- member component end -->
        </div>
      </div>
    </div>
    <device-message-modal
      :isButtonPermissionDisabled="isButtonPermissionDisabled"
      :defaultMessages="deviceData.default_message"
      v-if="renderComponent"
      @updateMessage="getDeviceMessage"
      :defaultMessage="deviceDefaultMessage"
    />
    <!-- End Message Bar -->
    <unlock-device-modal @unlockDeviceConfirmation="onUnlock" :device="deviceData.name"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axiosConfig from '@/services/axios.config';
import { OnImageError } from '@/utils/on-image-error';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseSkeleton from '@/components/base/BaseSkeleton.vue';
import UserIcon from '@/assets/icons/vue/user.vue';
import BackArrow from '@/assets/icons/vue/backArrow.vue';
import lockIcon from '@/assets/icons/svg/lock.svg';
import History from '@/assets/icons/vue/History.vue';
import Vector from '@/assets/icons/vue/vector.vue';
import Edit from '@/assets/icons/vue/edit.vue';
import Key from '@/assets/icons/vue/key.vue';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import DeviceMessageModal from '@/views/conference/modals/DeviceMessageModal.vue';
import UnlockDeviceModal from '@/components/modals/UnlockDeviceModal.vue';

import imageResizer from '@/utils/image-resizer';

export default {
  name: 'WorkRoom',
  components: {
    BackArrow,
    BaseButton,
    BaseSkeleton,
    UserIcon,
    Key,
    Edit,
    Vector,
    History,
    DeviceMessageModal,
    UnlockDeviceModal,
  },
  data() {
    return {
      lockIcon,
      OnImageError,
      deviceId: this.$route.params.id,
      deviceData: {},
      deviceMembers: [],
      imageSize: 150,
      renderComponent: true,
      deviceDefaultMessage: {},
    };
  },
  computed: {
    ...mapGetters({
      defaultCompany: 'getDefaultCompany',
      currentPermission: 'permissions/getPermissions',
      isLoading: 'loading/getActiveSkeletons',
    }),
    theme() {
      return this.$store.state.theme;
    },
    isOwnerAndAdmin() {
      return this.$store.state.permissions.isOwner ? true : this.deviceData.role === 'admin';
    },
    isOwnerAndNone() {
      return this.$store.state.permissions.isOwner ? false : this.deviceData.role === 'none';
    },
    isButtonPermissionDisabled() {
      if (!this.hasPermission('zones_and_devices_management') && this.isOwnerAndNone) {
        return true;
      }
      if (!this.hasPermission('zones_and_devices_management') && !this.isOwnerAndNone) {
        return false;
      }
      return false;
    },
  },
  mounted() {
    this.getSingleDevice();
    this.getDeviceMembers();
  },
  methods: {
    closeModals(...modals) {
      modals.map((modalName) => this.$store.dispatch('modal/close', modalName));
    },
    unlockDeviceHandler() {
      if (this.deviceData.can_unlock) {
        this.$store.dispatch('modal/open', 'unlockDeviceModal');
      }
    },
    openMessageModal() {
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
        this.$store.dispatch('modal/open', 'deviceMessageModal');
      });
    },
    getDeviceMessage() {
      axiosConfig
        .get(`/companies/${this.defaultCompany.id}/devices/${this.deviceId}/get-message`)
        .then((response) => {
          this.deviceDefaultMessage = !response.data.data ? {} : response.data.data;
        })
        .catch((error) => {
          serverErrorsHandler(error);
        });
    },
    resizer(image) {
      return imageResizer(image, this.imageSize);
    },
    hasPermission(permission) {
      return this.currentPermission.includes(permission);
    },
    onUnlock() {
      this.$store.commit('loading/DISABLE_HANDLER', true);
      axiosConfig
        .get(`/companies/${this.defaultCompany.id}/devices/${this.deviceId}/unlock`)
        .then(() => {
          this.closeModals('unlockDeviceModal');
          this.$toast.success(this.$t('unlocked_successfully', { device: this.deviceData.name }));
          this.$store.commit('loading/DISABLE_HANDLER', false);
        })
        .catch((error) => {
          this.closeModals('unlockDeviceModal');
          serverErrorsHandler(error);
          this.$store.commit('loading/DISABLE_HANDLER', false);
        });
    },
    getDeviceMembers() {
      axiosConfig
        .get(`/companies/${this.defaultCompany.id}/devices/${this.deviceId}/work-room-members`)
        .then((response) => {
          this.deviceMembers = response.data.data;
        });
    },
    getSingleDevice() {
      axiosConfig
        .get(`/companies/${this.defaultCompany.id}/devices/${this.deviceId}`)
        .then((response) => {
          this.deviceData = response.data.data;
          this.deviceDefaultMessage = this.deviceData.details;
          localStorage.setItem('activeDevice', JSON.stringify(this.deviceData));
        });
    },
  },
};
</script>

<style lang="css" scoped>
.back-icon {
  @apply flex-center w-50 h-50 rounded flex border-2 border-default left-50 cursor-pointer;
}
.unlock-button {
  @apply text-18 font-bold flex-center gap-5;
}
.flex-center {
  @apply flex items-center justify-center;
}
.device-icon-connected,
.device-icon-disconnected {
  @apply w-8 h-8 rounded-full opacity-40 shadow-icon;
}
.device-icon-connected {
  @apply bg-success;
}
.device-icon-disconnected {
  @apply bg-danger;
}
.top-bar {
  @apply flex flex-row-reverse items-center justify-between lg:flex-wrap lg:flex-row;
}
.member-card {
  @apply grid grid-cols-6 lg:grid-cols-6 md:grid-cols-4 sm:grid-cols-2 gap-x-26 gap-y-32;
}
.member-card-main {
  @apply relative bg-secondary shadow-deviceBoxShadow h-180 rounded;
}
.member-card-text {
  @apply text-center;
}
.member-card-image {
  @apply w-88 h-88 rounded-full object-cover;
}
.popover_classes {
  @apply cursor-pointer flex justify-evenly items-center
  text-12 font-semibold text-title;
}
.popover-img {
  @apply w-30;
}
.popover-bottom-bordered {
  @apply border-b border-default text-center;
}
.popover-group_li {
  @apply px-10 py-12 mx-10 my-5 rounded-full transition-all duration-300;
}
.hover-popover-group_li {
  @apply hover:bg-secondary;
}
.popover-title {
  @apply text-subtitle text-12 font-semibold;
}
.popover-text {
  @apply px-30 py-20 text-title;
}
.notification-box {
  @apply w-50 h-50 rounded border-2 border-default left-50
  cursor-pointer flex justify-center items-center;
}
.textarea {
  @apply resize-none rounded-sm py-10 px-50 w-full bg-secondary outline-none border
    border-primary;
}
.notification-logo {
  @apply absolute top-12 left-15;
}
.notification-logo:is(:lang(fa), :lang(ar)) {
  @apply right-15;
}
.flex-gap-margin {
  @apply mr-0 ml-11;
}
.flex-gap {
  @apply flex items-center justify-between w-full gap-x-20 lg:flex-col lg:gap-y-20;
}
</style>
